import {
  Avatar,
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  List as ListIcon,
} from "@mui/icons-material";
import BothIcon from "../icons/BothIcon";
import EmployeeIcon from "../icons/EmployeeIcon";
import EmployerIcon from "../icons/EmployerIcon";
import useAfasData from "../hooks/useAfasData";
import dayjs from "dayjs";
import { LanguageContext } from "../context/LanguageContext";

type AppointmentResponsible = "Employee" | "Manager" | "Both";

interface Appointment {
  id: number;
  employee_name: string;
  employee: string;
  responsible: AppointmentResponsible;
  description: string;
  date_agreement: string;
  deadline: null | string;
  status: string;
  status_code: "B" | "O" | "V"; // Behaald | Onderhandelen | Vervallen
  appraisal_interview: number;
  description_details: null | string;
}

const getIconByType = (type: AppointmentResponsible) => {
  switch (type) {
    case "Employee":
      return <EmployeeIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
    case "Manager":
      return <EmployerIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
    case "Both":
      return <BothIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
    default:
      return <BothIcon sx={{ fontSize: "1rem", width: "16px", height: "16px" }} />;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getIconColorByType = (type: AppointmentResponsible, theme: any) => {
  switch (type) {
    case "Employee":
      return theme.palette.lightblue.main;
    case "Manager":
      return theme.palette.primary.main;
    case "Both":
      return theme.palette.secondary.main;
    default:
      return theme.palette.secondary.main;
  }
};

interface Strings {
  interviews: {
    responsibleemployee: string;
    responsiblemanager: string;
    responsibleboth: string;
    unknown: string;
  };
}
function getNameOfResponsible(responsible: AppointmentResponsible, strings: Strings): string {
  switch (responsible) {
    case "Employee":
      return strings.interviews.responsibleemployee;
    case "Manager":
      return strings.interviews.responsiblemanager;
    case "Both":
      return strings.interviews.responsibleboth;
    default:
      return strings.interviews.unknown;
  }
}

const PopoverBackdrop = styled(Backdrop)({
  backgroundColor: "transparent",
});

const OngoingAppointmentsComponent: React.FC = () => {
  const { strings } = useContext(LanguageContext);
  const rowsPerPage = 5;
  const [page, setPage] = useState(0);
  const [ongoingAppointments, setOngoingAppointments] = useState<Appointment[]>([]);
  const { data: agreementsData, isLoading: agreementsDataLoading } = useAfasData("/agreements/overview");
  useEffect(() => {
    if (!agreementsDataLoading && agreementsData) {
      const agreements: Appointment[] = agreementsData.sort((a: Appointment, b: Appointment) => {
        if (a.deadline === null && b.deadline === null) return 0;
        if (a.deadline === null) return 1;
        if (b.deadline === null) return -1;
        return dayjs(a.deadline).isAfter(dayjs(b.deadline)) ? 1 : -1;
      });
      setOngoingAppointments(agreements);
    }
  }, [agreementsData]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Calculate pagination info
  const totalPages =
    Math.ceil(ongoingAppointments.length / rowsPerPage) > 0
      ? Math.ceil(ongoingAppointments.length / rowsPerPage)
      : 1;
  const currentPageStart = page * rowsPerPage;
  const currentPageEnd = Math.min((page + 1) * rowsPerPage, ongoingAppointments.length);
  const currentOngoingAppointments = ongoingAppointments.slice(currentPageStart, currentPageEnd);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Stack direction={"column"} sx={{ height: "100%" }}>
        <Stack mb={2} direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"}>
          <Typography variant="h6">{strings.interviews.agreements}</Typography>
          <Tooltip title={strings.interviews.legend} arrow placement="top">
            <IconButton size="small" onClick={handleClick}>
              <ListIcon />
            </IconButton>
          </Tooltip>
          <Popover
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            disableAutoFocus
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slots={{ backdrop: PopoverBackdrop }}
          >
            <Box p={2}>
              <Typography mb={0} ml={2} variant="overline" component={"p"}>
                {strings.interviews.legend}
              </Typography>
              {["Employee", "Manager", "Both"].map((resp) => (
                <MenuItem key={resp} onClick={handleClose}>
                  <Avatar
                    sx={(theme) => ({
                      width: "30px",
                      height: "30px",
                      bgcolor: getIconColorByType(resp as AppointmentResponsible, theme),
                      mr: 2,
                    })}
                  >
                    {getIconByType(resp as AppointmentResponsible)}{" "}
                  </Avatar>
                  {getNameOfResponsible(resp as AppointmentResponsible, strings as Strings)}
                </MenuItem>
              ))}
            </Box>
          </Popover>
        </Stack>
        <Box sx={{ flex: "1 1 auto", display: "flex", flexDirection: "column" }}>
          <List disablePadding sx={{ mb: 1 }}>
            {ongoingAppointments.length === 0 && !agreementsDataLoading && (
              <Typography variant="body1" color="inherit">
                {strings.interviews.noagreements}
              </Typography>
            )}
            {agreementsDataLoading && <CircularProgress />}
            {currentOngoingAppointments.map((appointment) => (
              <ListItem key={appointment.id} sx={{ pt: "0", pb: "1rem", px: 0, alignItems: "flex-start" }}>
                <ListItemAvatar sx={{ minWidth: "unset" }}>
                  <Tooltip
                    arrow
                    placement="top"
                    title={getNameOfResponsible(appointment.responsible, strings as Strings)}
                  >
                    <Avatar
                      sx={(theme) => ({
                        width: "30px",
                        height: "30px",
                        bgcolor: getIconColorByType(appointment.responsible, theme),
                        mr: 2,
                      })}
                    >
                      {getIconByType(appointment.responsible)}
                    </Avatar>
                  </Tooltip>
                </ListItemAvatar>
                <ListItemText
                  sx={{ mt: 0 }}
                  primary={`${appointment.employee_name}`}
                  secondary={
                    <Stack direction={"row"}>
                      <Tooltip arrow title={appointment.description_details} sx={{ fontWeight: 400 }}>
                        <span>
                          <Typography
                            sx={{
                              display: "-webkit-box",
                              "-webkit-line-clamp": "1",
                              lineClamp: "1",
                              "-webkit-box-orient": "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            component={"span"}
                            variant="inherit"
                          >
                            {appointment.description}
                          </Typography>
                        </span>
                      </Tooltip>
                    </Stack>
                  }
                />
                {appointment.deadline && (
                  <Typography variant="body1" sx={{ pl: 1, ml: "auto", whiteSpace: "nowrap" }}>
                    {dayjs(appointment.deadline).format("D-MM-YYYY")}
                  </Typography>
                )}
              </ListItem>
            ))}
          </List>
          <Stack direction="row" sx={{ justifyContent: "flex-end", alignItems: "center", mt: "auto" }}>
            {totalPages !== 1 && (
              <Box display="flex" alignItems="center">
                <IconButton
                  onClick={() => handleChangePage(null, page - 1)}
                  disabled={page === 0}
                  size="small"
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography component="span" sx={{ mx: 1 }}>
                  {page + 1}/
                  <Typography component={"span"} fontWeight={"bold"}>
                    {totalPages}
                  </Typography>
                </Typography>
                <IconButton
                  onClick={() => handleChangePage(null, page + 1)}
                  disabled={page >= totalPages - 1}
                  size="small"
                >
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
            )}
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default OngoingAppointmentsComponent;
